import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { BaseTool } from '../BaseTool';
import { WallElement } from '../../core/types';

export class AlignToolClass extends BaseTool {
    id = 'ALIGN';
    name = 'Align';
    cursor = 'crosshair';

    private sourceReference: { elementId: string, line: THREE.Line3 } | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = this.getWallIntersection(event);
        if (!hit) return;

        const { wall } = hit;

        // Determine which face/line of the wall is clicked
        // For simplicity, we'll use the centerline or the closest face
        // Let's assume centerline for now as it's easier to calculate from start/end
        const start = new THREE.Vector3(...wall.geometryData.start);
        const end = new THREE.Vector3(...wall.geometryData.end);
        const line = new THREE.Line3(start, end);

        if (!this.sourceReference) {
            // First click: Source Reference
            this.sourceReference = { elementId: wall.id, line };
            console.log('Align: Source selected', wall.id);
            // TODO: Visual feedback for source
        } else {
            // Second click: Target to align
            if (wall.id === this.sourceReference.elementId) return; // Can't align to self

            console.log('Align: Target selected', wall.id);
            this.alignWall(wall, this.sourceReference.line);

            // Reset or keep source? BIM keeps source active for multiple alignments.
            // We'll keep it.
        }
    }

    onPointerMove(): void {
        // Hover feedback
    }

    onPointerUp(): void {
    }

    private getWallIntersection(event: ThreeEvent<PointerEvent>) {
        const hit = this.findElementHitAtPoint(event, (el) => el.category === 'Walls');
        if (!hit) return null;
        return { wall: hit.element as WallElement, point: hit.point };
    }



    private alignWall(targetWall: WallElement, sourceLine: THREE.Line3) {
        // Project target wall's start and end points onto the source line (infinite line)
        // Actually, Align moves the target wall so it becomes collinear with source line
        // We need to calculate the translation vector.

        const targetStart = new THREE.Vector3(...targetWall.geometryData.start);
        const targetEnd = new THREE.Vector3(...targetWall.geometryData.end);

        // Project targetStart onto the line defined by sourceLine
        const projectedStart = new THREE.Vector3();
        sourceLine.closestPointToPoint(targetStart, true, projectedStart); // true = clamp? No, we want infinite line.
        // Line3.closestPointToPoint clamp argument: true to clamp to segment.
        // We want infinite line.

        // Custom projection for infinite line
        const lineDir = sourceLine.end.clone().sub(sourceLine.start).normalize();
        const v = targetStart.clone().sub(sourceLine.start);
        const d = v.dot(lineDir);
        const infiniteProjectedStart = sourceLine.start.clone().add(lineDir.multiplyScalar(d));

        const translation = infiniteProjectedStart.sub(targetStart);

        // Apply translation to target wall
        const newStart = targetStart.add(translation);
        const newEnd = targetEnd.add(translation);

        this.context!.bimStore.updateElement(targetWall.id, {
            geometryData: {
                ...targetWall.geometryData,
                start: [newStart.x, newStart.y, newStart.z],
                end: [newEnd.x, newEnd.y, newEnd.z]
            }
        });
    }

    deactivate() {
        super.deactivate();
        this.sourceReference = null;
    }
}
